<template>
  <a-drawer
    :class="['monitor-drawer', { 'monitor-drawer--dark': isDark }]"
    placement="bottom"
    :height="drawerHeight"
    :closable="false"
    :visible="visible"
    :mask="false"
    :body-style="{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }"
  >
    <!-- Drag handle -->
    <div class="monitor-handle-bar" @click="$emit('close')">
      <span class="monitor-handle-pill"></span>
    </div>

    <!-- Header -->
    <div class="monitor-header">
      <div class="monitor-header__left">
        <a-icon type="desktop" class="monitor-header__icon" />
        <span class="monitor-header__title">{{ $t('monitor.title') }}</span>
        <span :class="['monitor-status-badge', connected ? 'monitor-status-badge--live' : 'monitor-status-badge--off']">
          <span class="monitor-status-badge__dot"></span>
          <span class="monitor-status-badge__label">{{ connected ? $t('monitor.status.live') : $t('monitor.status.disconnected') }}</span>
        </span>
      </div>
      <div class="monitor-header__right">
        <a-tooltip :title="isFullscreen ? $t('monitor.fullscreen.exit') : $t('monitor.fullscreen.enter')" placement="bottomRight">
          <button class="monitor-btn-icon" @click="toggleFullscreen">
            <a-icon :type="isFullscreen ? 'fullscreen-exit' : 'fullscreen'" />
          </button>
        </a-tooltip>
        <a-tooltip :title="isDark ? $t('monitor.theme.light') : $t('monitor.theme.dark')" placement="bottomRight">
          <button :class="['monitor-btn-theme', { 'monitor-btn-theme--active': isDark }]" @click="toggleTheme">
            <a-icon type="bulb" :theme="isDark ? 'filled' : 'outlined'" />
          </button>
        </a-tooltip>
        <button class="monitor-btn-close" @click="$emit('close')">
          <a-icon type="down" />
          <span>{{ $t('monitor.close') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters bar -->
    <div class="monitor-filters">
      <div class="monitor-filters__left">
        <monitor-saved-views
          ref="savedViews"
          :current-filters="serializedFilters"
          @apply="applyFilters"
        />

        <div class="monitor-filters__divider"></div>

        <div class="mf-segment-group">
          <a-select
            v-model="selectedSegments"
            mode="multiple"
            :placeholder="$t('monitor.filter.segments')"
            :max-tag-count="2"
            class="mf-input mf-input--md"
            :allow-clear="true"
            @change="onSegmentFilter"
          >
            <a-select-option v-for="seg in segmentOptions" :key="seg.id" :value="seg.id">
              {{ seg.name }}
            </a-select-option>
          </a-select>
          <a-tooltip :title="segmentsInverse ? $t('monitor.filter.inverseSegmentsActive') : $t('monitor.filter.inverseSegments')">
            <a-button
              class="mf-btn mf-btn--inverse"
              :type="segmentsInverse ? 'primary' : 'default'"
              :disabled="!selectedSegments.length"
              @click="toggleSegmentsInverse"
            >
              <a-icon type="swap" />
            </a-button>
          </a-tooltip>
        </div>

        <a-select
          v-model="selectedUsers"
          mode="multiple"
          :placeholder="$t('monitor.filter.agents')"
          :max-tag-count="2"
          class="mf-input mf-input--md"
          :allow-clear="true"
          @change="onUserFilter"
        >
          <a-select-option v-for="user in userOptions" :key="user.id" :value="user.id">
            {{ user.firstname }} {{ user.lastname }}
          </a-select-option>
        </a-select>

        <a-input-search
          v-model="searchQuery"
          :placeholder="$t('monitor.filter.search')"
          class="mf-input mf-input--search"
          :allow-clear="true"
          @change="onSearch"
        />

        <a-tooltip :title="$t('monitor.filter.topTooltip', { count: topN })">
          <a-checkbox
            class="mf-top"
            :checked="topActive"
            :disabled="hasUserFilters"
            @change="onTopToggle"
          >
            {{ $t('monitor.filter.top', { count: topN }) }}
          </a-checkbox>
        </a-tooltip>
      </div>

      <div class="monitor-filters__divider"></div>

      <div class="monitor-filters__right">
        <span class="mf-detail-label">{{ $t('monitor.detail.label') }}</span>
        <a-checkbox-group
          v-model="detailLevels"
          class="mf-detail"
          @change="onDetailChange"
        >
          <a-checkbox value="agents">{{ $t('monitor.detail.agents') }}</a-checkbox>
          <a-checkbox value="queues">{{ $t('monitor.detail.queues') }}</a-checkbox>
          <a-checkbox value="calls">{{ $t('monitor.detail.calls') }}</a-checkbox>
        </a-checkbox-group>

        <div class="monitor-filters__divider"></div>

        <a-select
          v-model="sortField"
          :placeholder="$t('monitor.sort.placeholder')"
          class="mf-input mf-input--sort"
          :allow-clear="true"
          @change="onSort"
        >
          <a-select-option value="available">{{ $t('monitor.sort.available') }}</a-select-option>
          <a-select-option value="waiting">{{ $t('monitor.sort.waiting') }}</a-select-option>
          <a-select-option value="inCall">{{ $t('monitor.sort.inCall') }}</a-select-option>
          <a-select-option value="acw">{{ $t('monitor.sort.acw') }}</a-select-option>
          <a-select-option value="occupied">{{ $t('monitor.sort.occupied') }}</a-select-option>
          <a-select-option value="online">{{ $t('monitor.sort.online') }}</a-select-option>
          <a-select-option value="offline">{{ $t('monitor.sort.offline') }}</a-select-option>
          <a-select-option value="total">{{ $t('monitor.sort.total') }}</a-select-option>
        </a-select>

        <a-tooltip :title="sortOrder === 1 ? $t('monitor.sort.asc') : $t('monitor.sort.desc')">
          <a-button
            class="mf-btn"
            :disabled="!sortField"
            @click="toggleSortOrder"
          >
            <a-icon :type="sortOrder === 1 ? 'sort-ascending' : 'sort-descending'" />
          </a-button>
        </a-tooltip>

        <a-button class="mf-btn mf-btn--reset" @click="resetFilters">
          <a-icon type="close-circle" />
          <span class="mf-btn__label">{{ $t('monitor.filter.reset') }}</span>
        </a-button>
      </div>
    </div>

    <!-- Web Component -->
    <div class="monitor-chart-container">
      <videsk-monitor ref="monitorEl" detail="none"></videsk-monitor>
    </div>
  </a-drawer>
</template>

<script>
import MonitorSavedViews from './MonitorSavedViews.vue';

export default {
  name: 'MonitorDrawer',
  components: { MonitorSavedViews },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    drawerHeight: '70vh',
    isDark: false,
    isFullscreen: false,
    selectedSegments: [],
    selectedUsers: [],
    segmentsInverse: false,
    searchQuery: '',
    sortField: undefined,
    sortOrder: -1,
    topActive: true,
    topN: 10,
    detailLevels: [],
  }),
  computed: {
    connected() {
      return this.$store.getters['live-monitor/connected'];
    },
    segments() {
      return this.$store.getters['live-monitor/segments'];
    },
    users() {
      return this.$store.getters['live-monitor/users'];
    },
    queues() {
      return this.$store.getters['live-monitor/queues'];
    },
    calls() {
      return this.$store.getters['live-monitor/calls'];
    },
    segmentOptions() {
      return this.segments?.map(s => ({ id: s.id, name: s.name }));
    },
    userOptions() {
      return this.users?.map(u => ({ id: u.id, firstname: u.firstname, lastname: u.lastname }));
    },
    hasUserFilters() {
      return this.selectedSegments.length > 0 || this.selectedUsers.length > 0 || !!this.searchQuery.trim();
    },
    // Single source of truth that gets persisted into a saved view. Order of
    // keys is stable so JSON.stringify can be used for cheap deep-equality.
    serializedFilters() {
      return {
        segments: [...this.selectedSegments],
        segmentsInverse: this.segmentsInverse,
        users: [...this.selectedUsers],
        searchQuery: this.searchQuery,
        sortField: this.sortField || null,
        sortOrder: this.sortOrder,
        topActive: this.topActive,
        detailLevels: [...this.detailLevels],
      };
    },
    // Single computed used as watcher target to guarantee setter order.
    // segments must always be assigned last - see docs/adr/001-setter-ordering.md
    monitorData() {
      return {
        users: this.users,
        queues: this.queues,
        calls: this.calls,
        segments: this.segments,
      };
    },
  },
  watch: {
    monitorData(data) {
      // Drawer content is lazy-rendered: the ref may not exist yet if the
      // drawer has never been opened. $nextTick ensures we wait for the DOM
      // update triggered by the Vuex mutation before accessing the ref.
      this.$nextTick(() => this._pushMonitorData(data));
    },
    isDark(val) {
      // Theme is an observed attribute on the web component, so we must use
      // setAttribute rather than a Vue prop binding.
      this.$nextTick(() => {
        const el = this.$refs.monitorEl;
        if (el) el.setAttribute('theme', val ? 'dark' : 'light');
      });
    },
    visible(isOpen) {
      if (!isOpen) return;
      // When the drawer opens (first or subsequent time), the slot content is
      // inserted into the DOM on this tick. Retry via $nextTick until the ref
      // is available, then flush the current store snapshot and resize.
      const tryFlush = () => {
        const el = this.$refs.monitorEl;
        if (!el) { this.$nextTick(tryFlush); return; }
        this._pushMonitorData(this.monitorData);
        el.setAttribute('theme', this.isDark ? 'dark' : 'light');
        this._applyTop();
        this._applyDetail();
        if (typeof el.resize === 'function') el.resize();
      };
      this.$nextTick(tryFlush);
    },
    hasUserFilters(active) {
      // Top N auto-disables the moment the user applies any filter,
      // so the visible result matches whatever filter is in effect.
      if (active && this.topActive) {
        this.topActive = false;
        this._applyTop();
      }
    },
  },
  mounted() {
    this._fsHandler = () => {
      this.isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
    };
    document.addEventListener('fullscreenchange', this._fsHandler);
    document.addEventListener('webkitfullscreenchange', this._fsHandler);
  },
  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this._fsHandler);
    document.removeEventListener('webkitfullscreenchange', this._fsHandler);
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (exit) exit.call(document);
    }
    clearTimeout(this._centerTimer);
  },
  methods: {
    _pushMonitorData({ users, queues, calls, segments }) {
      const el = this.$refs.monitorEl;
      if (!el) return;
      // Order is mandatory: users → queues → calls → segments (last)
      // segments setter reads the other three nodes synchronously.
      // See docs/adr/001-setter-ordering.md
      el.users = users;
      el.queues = queues;
      el.calls = calls;
      el.segments = segments;
    },
    onSegmentFilter(values) {
      const el = this.$refs.monitorEl;
      if (!el) return;
      el.filterBy('segments', values.length ? values : null, this.segmentsInverse);
      this._scheduleCenter();
    },
    onUserFilter(values) {
      const el = this.$refs.monitorEl;
      if (!el) return;
      el.filterBy('users', values.length ? values : null);
      this._scheduleCenter();
    },
    onSearch() {
      const el = this.$refs.monitorEl;
      if (!el) return;
      const query = this.searchQuery.trim();
      el.filterBy('search', query || null);
      this._scheduleCenter();
    },
    onSort() {
      const el = this.$refs.monitorEl;
      if (!el || !this.sortField) return;
      el.sortBy(this.sortField, this.sortOrder);
      this._scheduleCenter();
    },
    onTopToggle(e) {
      this.topActive = e.target.checked;
      this._applyTop();
      this._scheduleCenter();
    },
    onDetailChange() {
      this._applyDetail();
      this._scheduleCenter();
    },
    toggleSegmentsInverse() {
      this.segmentsInverse = !this.segmentsInverse;
      this.onSegmentFilter(this.selectedSegments);
    },
    toggleTheme() {
      this.isDark = !this.isDark;
    },
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === 1 ? -1 : 1;
      this.onSort();
    },
    toggleFullscreen() {
      if (!this.isFullscreen) {
        const el = document.querySelector('.monitor-drawer .ant-drawer-content-wrapper');
        if (el) {
          const req = el.requestFullscreen || el.webkitRequestFullscreen;
          if (req) req.call(el);
        }
      } else {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit) exit.call(document);
      }
    },
    resetFilters() {
      this.selectedSegments = [];
      this.selectedUsers = [];
      this.segmentsInverse = false;
      this.searchQuery = '';
      this.sortField = undefined;
      this.sortOrder = -1;
      this.topActive = true;
      this.detailLevels = [];
      if (this.$refs.savedViews) this.$refs.savedViews.clearActive();
      const el = this.$refs.monitorEl;
      if (!el) return;
      el.resetFilters();
      this._applyTop();
      this._applyDetail();
      this._scheduleCenter();
    },
    // Hydrate every filter dimension from a saved-view payload and push the
    // resulting state into the web component. Unknown keys are ignored;
    // missing keys fall back to current defaults so older saved views still
    // load cleanly on newer clients.
    applyFilters(payload = {}) {
      const f = payload || {};
      this.selectedSegments = Array.isArray(f.segments) ? [...f.segments] : [];
      this.selectedUsers = Array.isArray(f.users) ? [...f.users] : [];
      this.segmentsInverse = !!f.segmentsInverse;
      this.searchQuery = typeof f.searchQuery === 'string' ? f.searchQuery : '';
      this.sortField = f.sortField || undefined;
      this.sortOrder = f.sortOrder === 1 ? 1 : -1;
      this.topActive = typeof f.topActive === 'boolean' ? f.topActive : true;
      this.detailLevels = Array.isArray(f.detailLevels) ? [...f.detailLevels] : [];

      const el = this.$refs.monitorEl;
      if (!el) return;
      el.resetFilters();
      el.filterBy('segments', this.selectedSegments.length ? this.selectedSegments : null, this.segmentsInverse);
      el.filterBy('users', this.selectedUsers.length ? this.selectedUsers : null);
      const query = this.searchQuery.trim();
      el.filterBy('search', query || null);
      if (this.sortField) el.sortBy(this.sortField, this.sortOrder);
      this._applyTop();
      this._applyDetail();
      this._scheduleCenter();
    },
    _applyTop() {
      const el = this.$refs.monitorEl;
      if (!el) return;
      el.setAttribute('top', String(this.topActive && !this.hasUserFilters ? this.topN : 0));
    },
    _applyDetail() {
      const el = this.$refs.monitorEl;
      if (!el) return;
      el.setAttribute('detail', this.detailLevels.length ? this.detailLevels.join(',') : 'none');
    },
    _scheduleCenter() {
      clearTimeout(this._centerTimer);
      this._centerTimer = setTimeout(() => {
        const el = this.$refs.monitorEl;
        if (el && typeof el.center === 'function') el.center();
      }, 1500);
    },
  },
};
</script>

<style>
/* ── Drawer shell ─────────────────────────────────────────────────── */
.monitor-drawer .ant-drawer-content-wrapper,
.monitor-drawer .ant-drawer-content {
  border-radius: 12px 12px 0 0;
  overflow: hidden;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.12);
}

.monitor-drawer .ant-drawer-body {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Drag handle ──────────────────────────────────────────────────── */
.monitor-handle-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 20px;
  background: #1d2030;
  cursor: pointer;
  flex-shrink: 0;
}

.monitor-handle-pill {
  width: 36px;
  height: 4px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 99px;
  transition: background 0.15s;
}

.monitor-handle-bar:hover .monitor-handle-pill {
  background: rgba(255, 255, 255, 0.5);
}

/* ── Header ───────────────────────────────────────────────────────── */
.monitor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  height: 48px;
  background: #1d2030;
  flex-shrink: 0;
}

.monitor-header__left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.monitor-header__icon {
  color: rgba(255, 255, 255, 0.5);
  font-size: 15px;
  flex-shrink: 0;
}

.monitor-header__title {
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
}

/* Connection status badge inside header */
.monitor-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px 2px 6px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}

.monitor-status-badge--live {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
}

.monitor-status-badge--off {
  background: rgba(107, 114, 128, 0.2);
  color: #9ca3af;
}

.monitor-status-badge__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.monitor-status-badge--live .monitor-status-badge__dot {
  background: #10b981;
  animation: pulse-green 2s infinite;
}

.monitor-status-badge--off .monitor-status-badge__dot {
  background: #6b7280;
}

@keyframes pulse-green {
  0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5); }
  50% { box-shadow: 0 0 0 4px rgba(16, 185, 129, 0); }
}

/* Header right group */
.monitor-header__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* Fullscreen icon button */
.monitor-btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 15px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  padding: 0;
}

.monitor-btn-icon:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.3);
}

/* Theme toggle button */
.monitor-btn-theme {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 15px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  padding: 0;
}

.monitor-btn-theme:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.3);
}

.monitor-btn-theme--active {
  background: rgba(251, 191, 36, 0.15);
  border-color: rgba(251, 191, 36, 0.4);
  color: #fbbf24;
}

.monitor-btn-theme--active:hover {
  background: rgba(251, 191, 36, 0.25);
  border-color: rgba(251, 191, 36, 0.6);
  color: #fcd34d;
}

/* Close / minimize button - intentionally large and labeled */
.monitor-btn-close {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
}

.monitor-btn-close:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.35);
}

/* Remove rounded corners when browser fullscreen is active */
.ant-drawer-content-wrapper:fullscreen,
.ant-drawer-content-wrapper:-webkit-full-screen {
  border-radius: 0 !important;
  height: 100% !important;
}

.ant-drawer-content-wrapper:fullscreen .ant-drawer-content,
.ant-drawer-content-wrapper:-webkit-full-screen .ant-drawer-content {
  border-radius: 0;
  height: 100%;
}

/* ── Filters bar ──────────────────────────────────────────────────── */
.monitor-filters {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 8px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  flex-wrap: wrap;
  row-gap: 6px;
}

.monitor-filters__left,
.monitor-filters__right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.monitor-filters__left { flex: 1; min-width: 0; }
.monitor-filters__right { flex-shrink: 0; }

.monitor-filters__divider {
  width: 1px;
  height: 24px;
  background: #d1d5db;
  margin: 0 10px;
  flex-shrink: 0;
}

/* Common input sizing - natural width, not stretched */
.mf-input.ant-select,
.mf-input.ant-input-search {
  width: auto;
}

.mf-input--md { min-width: 150px; max-width: 200px; }
.mf-input--search { min-width: 160px; max-width: 220px; }
.mf-input--sort { min-width: 140px; max-width: 180px; }

.mf-btn.ant-btn {
  flex-shrink: 0;
  padding: 0 10px;
}

/* Reset button: icon + label, label hidden on small screens */
.mf-btn--reset .mf-btn__label { margin-left: 4px; }

/* Segment select + inverse toggle stay glued together */
.mf-segment-group {
  display: flex;
  align-items: stretch;
  gap: 2px;
}

.mf-btn--inverse.ant-btn { padding: 0 8px; }

/* Top N checkbox */
.mf-top.ant-checkbox-wrapper {
  margin-left: 4px;
  font-size: 13px;
  white-space: nowrap;
  user-select: none;
}

/* Detail-level checkbox group */
.mf-detail-label {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

.mf-detail.ant-checkbox-group {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.mf-detail.ant-checkbox-group .ant-checkbox-wrapper {
  margin-right: 0;
  margin-left: 0;
}

/* ── Chart area ───────────────────────────────────────────────────── */
.monitor-chart-container {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.monitor-chart-container videsk-monitor {
  display: block;
  width: 100%;
  height: 100%;
}

/* ── Responsive ───────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .monitor-header__title { font-size: 13px; }

  .monitor-status-badge__label { display: none; }
  .monitor-status-badge { padding: 2px 5px; }

  .monitor-filters {
    padding: 6px 10px;
  }

  .monitor-filters__divider { display: none; }

  .monitor-filters__left,
  .monitor-filters__right {
    flex-basis: 100%;
  }

  .mf-input--md,
  .mf-input--search,
  .mf-input--sort {
    min-width: 0;
    flex: 1;
    max-width: none;
  }

  .mf-btn--reset .mf-btn__label { display: none; }
}

@media (max-width: 480px) {
  .monitor-btn-close span { display: none; }
  .monitor-btn-close { padding: 6px 10px; }
}

/* ── Dark mode - filter bar ───────────────────────────────────────── */
.monitor-drawer--dark .monitor-filters {
  background: #181b2a;
  border-bottom-color: #2d3148;
}

.monitor-drawer--dark .monitor-filters__divider {
  background: #2d3148;
}

/* Ant Design Select */
.monitor-drawer--dark .mf-input .ant-select-selection {
  background: #1e2235;
  border-color: #2d3148;
  color: #c9d1e0;
}

.monitor-drawer--dark .mf-input .ant-select-selection:hover,
.monitor-drawer--dark .mf-input .ant-select-focused .ant-select-selection {
  border-color: #4b5585;
}

.monitor-drawer--dark .mf-input .ant-select-selection__placeholder,
.monitor-drawer--dark .mf-input .ant-select-search__field__placeholder {
  color: #4b5585;
}

.monitor-drawer--dark .mf-input .ant-select-arrow,
.monitor-drawer--dark .mf-input .ant-select-selection__clear {
  color: #4b5585;
}

.monitor-drawer--dark .mf-input .ant-select-selection--multiple .ant-select-selection__choice {
  background: #2d3148;
  border-color: #3d4460;
  color: #c9d1e0;
}

/* Ant Design Input */
.monitor-drawer--dark .mf-input .ant-input,
.monitor-drawer--dark .mf-input .ant-input-search-icon {
  background: #1e2235;
  border-color: #2d3148;
  color: #c9d1e0;
}

.monitor-drawer--dark .mf-input .ant-input:hover,
.monitor-drawer--dark .mf-input .ant-input:focus {
  border-color: #4b5585;
  box-shadow: none;
}

.monitor-drawer--dark .mf-input .ant-input::placeholder {
  color: #4b5585;
}

.monitor-drawer--dark .mf-input .ant-input-suffix .anticon {
  color: #4b5585;
}

/* Ant Design Buttons */
.monitor-drawer--dark .mf-btn.ant-btn {
  background: #1e2235;
  border-color: #2d3148;
  color: #c9d1e0;
}

.monitor-drawer--dark .mf-btn.ant-btn:hover:not([disabled]) {
  background: #252836;
  border-color: #4b5585;
  color: #fff;
}

.monitor-drawer--dark .mf-btn.ant-btn[disabled] {
  background: #181b2a;
  border-color: #2d3148;
  color: #2d3148;
}

/* Dark-mode checkboxes (Top N and detail levels) */
.monitor-drawer--dark .mf-top.ant-checkbox-wrapper,
.monitor-drawer--dark .mf-detail.ant-checkbox-group .ant-checkbox-wrapper,
.monitor-drawer--dark .mf-detail-label {
  color: #c9d1e0;
}

.monitor-drawer--dark .mf-top .ant-checkbox-inner,
.monitor-drawer--dark .mf-detail .ant-checkbox-inner {
  background: #1e2235;
  border-color: #4b5585;
}

.monitor-drawer--dark .mf-top .ant-checkbox-disabled .ant-checkbox-inner,
.monitor-drawer--dark .mf-detail .ant-checkbox-disabled .ant-checkbox-inner {
  background: #181b2a;
  border-color: #2d3148;
}

.monitor-drawer--dark .mf-top .ant-checkbox-disabled + span,
.monitor-drawer--dark .mf-detail .ant-checkbox-disabled + span {
  color: #4b5585;
}
</style>
